import { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { message } from 'antd';
import { useAppointmentList } from '../hooks/useAppointmentList';
import { useAuth } from '../../../contexts/AuthContext';
import { appointmentService } from '../../../services/appointmentService';
import EmptyState from '../../../components/ui/EmptyState';
import ConfirmModal from '../../../components/common/ConfirmModal';
import Breadcrumb from '../../../components/ui/Breadcrumb';
import { APPOINTMENT_STATUS_BADGE } from '../../../domain/statuses';
import { formatDate } from '../../../lib/format';

const AppointmentListPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const {
    appointments,
    loading,
    error,
    pagination,
    searchTerm,
    pendingDeleteId,
    fetchAppointments,
    updateSearch,
    deleteAppointment,
    cancelDelete,
    confirmDelete,
    goToPage,
  } = useAppointmentList();

  useEffect(() => {
    fetchAppointments();
  }, [fetchAppointments]);

  const handleStartConsultation = async (appointmentId: number) => {
    if (user?.doctor) {
      navigate(`/consultations/new?appointment_id=${appointmentId}`);
      return;
    }

    try {
      await appointmentService.startConsultation(appointmentId);
      message.success('Consulta iniciada');
      await fetchAppointments();
    } catch {
      message.error('No se pudo iniciar la consulta');
    }
  };

  return (
    <>
      <div className="page-header">
        <div className="row">
          <div className="col-sm-12">
            <Breadcrumb items={[{ label: 'Inicio', to: '/' }, { label: 'Citas' }]} />
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-sm-12">
          <div className="card">
            <div className="card-body">
              <div className="row mb-3">
                <div className="col-md-6">
                  <div className="input-group">
                    <span className="input-group-text"><i className="fa fa-search"></i></span>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Buscar por paciente, doctor o motivo..."
                      value={searchTerm}
                      onChange={e => updateSearch(e.target.value)}
                    />
                  </div>
                </div>
                <div className="col-md-6 text-end">
                  <Link to="/appointments/new" className="btn btn-primary">
                    <i className="fa fa-plus me-1"></i> Nueva Cita
                  </Link>
                </div>
              </div>

              {error && <div className="alert alert-danger">{error}</div>}

              {loading ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Cargando...</span>
                  </div>
                </div>
              ) : appointments.length === 0 ? (
                searchTerm ? (
                  <EmptyState icon="fa-search" title="Sin resultados para la búsqueda" />
                ) : (
                  <EmptyState icon="fa-calendar-times" title="No hay citas registradas" action={{ label: 'Nueva Cita', to: '/appointments/new', icon: 'fa-plus' }} />
                )
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover mb-0">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Paciente</th>
                        <th>Doctor</th>
                        <th>Fecha</th>
                        <th>Hora</th>
                        <th>Motivo</th>
                        <th>Estado</th>
                        <th className="text-center">Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {appointments.map(appt => (
                        <tr key={appt.id}>
                          <td>
                            <span className="text-muted" style={{ fontSize: '0.85em' }}>
                              {appt.appointment_number || `#${appt.id}`}
                            </span>
                          </td>
                          <td>
                            <strong>
                              {appt.patient
                                ? `${appt.patient.first_name} ${appt.patient.last_name}`
                                : '-'}
                            </strong>
                            {appt.patient?.identification && (
                              <div className="text-muted" style={{ fontSize: '0.85em' }}>
                                {appt.patient.identification}
                              </div>
                            )}
                          </td>
                          <td>
                            {appt.doctor
                              ? `Dr. ${appt.doctor.first_name} ${appt.doctor.last_name}`
                              : '-'}
                          </td>
                          <td>{formatDate(appt.appointment_date)}</td>
                          <td>{appt.appointment_time || '-'}</td>
                          <td>
                            <span title={appt.reason} style={{ maxWidth: 200, display: 'inline-block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {appt.reason || '-'}
                            </span>
                          </td>
                          <td>
                            <span className={APPOINTMENT_STATUS_BADGE[appt.status_code] || 'custom-badge status-grey'}>
                              {appt.status_name || 'Sin estado'}
                            </span>
                          </td>
                          <td className="text-center">
                            <div className="d-flex justify-content-center gap-1">
                              <Link
                                to={`/appointments/${appt.id}`}
                                className="btn btn-sm bg-info-light"
                                title="Ver Detalle"
                              >
                                <i className="fa fa-eye"></i>
                              </Link>
                              {(appt.status_code === 'SCHEDULED' ||
                                appt.status_code === 'CONFIRMED' ||
                                appt.status_code === 'WAITING') && (
                                <button
                                  className="btn btn-sm bg-success-light"
                                  title="Iniciar Consulta"
                                  onClick={() => handleStartConsultation(appt.id)}
                                >
                                  <i className="fa fa-stethoscope"></i>
                                </button>
                              )}
                              <button
                                className="btn btn-sm bg-danger-light"
                                title="Eliminar"
                                onClick={() => deleteAppointment(appt.id)}
                              >
                                <i className="fa fa-trash"></i>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {pagination.totalPages > 1 && (
                <div className="d-flex justify-content-between align-items-center mt-3">
                  <span className="text-muted" style={{ fontSize: '0.9em' }}>
                    Mostrando {appointments.length} de {pagination.total} citas
                  </span>
                  <ul className="pagination mb-0">
                    <li className={`page-item ${pagination.currentPage === 1 ? 'disabled' : ''}`}>
                      <button className="page-link" onClick={() => goToPage(pagination.currentPage - 1)}>
                        <i className="fa fa-chevron-left"></i>
                      </button>
                    </li>
                    {Array.from({ length: pagination.totalPages }, (_, i) => i + 1)
                      .filter(p => Math.abs(p - pagination.currentPage) <= 2)
                      .map(p => (
                        <li key={p} className={`page-item ${p === pagination.currentPage ? 'active' : ''}`}>
                          <button className="page-link" onClick={() => goToPage(p)}>{p}</button>
                        </li>
                      ))}
                    <li className={`page-item ${pagination.currentPage === pagination.totalPages ? 'disabled' : ''}`}>
                      <button className="page-link" onClick={() => goToPage(pagination.currentPage + 1)}>
                        <i className="fa fa-chevron-right"></i>
                      </button>
                    </li>
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <ConfirmModal
        show={pendingDeleteId !== null}
        title="Eliminar cita"
        body="¿Está seguro de eliminar esta cita? Esta acción no se puede deshacer."
        confirmLabel="Eliminar"
        confirmVariant="danger"
        loading={loading}
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
      />
    </>
  );
};

export default AppointmentListPage;
